import { CheckCircleOutlined, WarningAmberOutlined, ExpandLess, ExpandMore } from '@mui/icons-material';
import { ChipProps, SvgIcon, Chip, IconButton, Typography, Collapse, Stack } from '@mui/material';
import { ExtendedList, ExtendedListItem, ExtendedListItemText, NestedList, useCurrency } from '@procureai/ui-mui';
import { isEmpty, isNotEmpty, isNotNil } from 'ramda';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IntakeExtendedLineItemsCheckStepFormData } from '../IntakeExtendedLineItemsCheckStep.schema';
import { SvbrgExtendedLineItemsCheck } from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types';

import { CatalogueResultsGrid } from './CatalogueResultsGrid';
import { ContractItemsList } from './ContractItemsList';

type RiskLevel = 'LOW' | 'MEDIUM' | 'RESOLVED';
const propsByRiskMap: {
  [key in RiskLevel]: {
    color: ChipProps['color'];
    Icon: typeof SvgIcon;
    severity: 'success' | 'warning' | 'info';
    translationKey: string;
  };
} = {
  LOW: {
    color: 'success',
    Icon: CheckCircleOutlined,
    severity: 'success',
    translationKey: 'low',
  },
  MEDIUM: {
    color: 'warning',
    Icon: WarningAmberOutlined,
    severity: 'warning',
    translationKey: 'medium',
  },
  RESOLVED: {
    color: 'info',
    Icon: CheckCircleOutlined,
    severity: 'info',
    translationKey: 'resolved',
  },
};

export const ExtendedLineItem = ({ check }: { check: SvbrgExtendedLineItemsCheck }) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeExtendedLineItemsCheckStep' });
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const catalogueItems = check.checkResults?.catalogueItems;
  const contractItems = check.checkResults?.contractItems;

  return (
    <>
      <ListItemCard check={check} isDetailsExpanded={isDetailsExpanded} setIsDetailsExpanded={setIsDetailsExpanded} />
      <Collapse in={isDetailsExpanded}>
        {!isEmpty(catalogueItems) || !isEmpty(contractItems) ? (
          <NestedList>
            <ExtendedList>
              <ExtendedListItem>
                <CatalogueResultsGrid checkId={check.id} results={catalogueItems!} />
              </ExtendedListItem>
              <ExtendedListItem>
                <ContractItemsList checkId={check.id} items={contractItems!} />
              </ExtendedListItem>
            </ExtendedList>
          </NestedList>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {t('noResults')}
          </Typography>
        )}
      </Collapse>
    </>
  );
};

const ListItemCard = ({
  check,
  isDetailsExpanded,
  setIsDetailsExpanded,
}: {
  check: SvbrgExtendedLineItemsCheck;
  isDetailsExpanded: boolean;
  setIsDetailsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeExtendedLineItemsCheckStep' });

  const { watch } = useFormContext<IntakeExtendedLineItemsCheckStepFormData>();
  const selectedItem = watch(`items.${check.id}`);
  const { format } = useCurrency({
    code: selectedItem && typeof selectedItem !== 'string' ? selectedItem.currency : 'EUR',
  });

  // Determine risk level based on presence of results
  const checkResults = check.checkResults;
  const riskLevel: RiskLevel =
    isNotNil(checkResults) && (isNotEmpty(checkResults.catalogueItems) || isNotEmpty(checkResults.contractItems))
      ? selectedItem
        ? 'RESOLVED'
        : 'MEDIUM'
      : 'LOW';
  const { color, Icon, translationKey } = propsByRiskMap[riskLevel];
  const title = selectedItem ? (
    <Stack
      spacing={1}
      direction={'row'}
      divider={
        <Typography component={'h6'} variant="subtitle2">
          |
        </Typography>
      }
    >
      <Typography component={'h6'} variant="subtitle2" noWrap>
        {`${check.position}. `}
        {selectedItem.title}
      </Typography>
      {selectedItem.pricePerUnit && (
        <Typography component={'h6'} variant="subtitle2">
          {t('price')}: {format(parseFloat(selectedItem.pricePerUnit))}
        </Typography>
      )}
      {check.quantity && (
        <Typography component={'h6'} variant="subtitle2">
          {t('quantity')}: {check.quantity}
        </Typography>
      )}
    </Stack>
  ) : (
    <Stack
      spacing={1}
      direction={'row'}
      divider={
        <Typography component={'h6'} variant="subtitle2">
          |
        </Typography>
      }
    >
      <Typography component={'h6'} variant="subtitle2" noWrap>
        {`${check.position}. `} {check.title}
      </Typography>
      {check.pricePerUnit && (
        <Typography component={'h6'} variant="subtitle2">
          {t('price')}: {format(parseFloat(check.pricePerUnit))}
        </Typography>
      )}
      {check.quantity && (
        <Typography component={'h6'} variant="subtitle2">
          {t('quantity')}: {check.quantity}
        </Typography>
      )}
    </Stack>
  );
  const description =
    selectedItem && typeof selectedItem !== 'string' ? (
      selectedItem.description ? (
        <Typography variant="caption">{selectedItem.description}</Typography>
      ) : null
    ) : check.description ? (
      <Typography variant="caption">{check.description}</Typography>
    ) : null;

  const deliveryInfo =
    selectedItem &&
    typeof selectedItem !== 'string' &&
    (selectedItem.earlyDeliveryAt || selectedItem.lateDeliveryAt) ? (
      <Typography variant="caption">
        {t('delivery')}:
        {selectedItem.earlyDeliveryAt && ` ${new Date(selectedItem.earlyDeliveryAt).toLocaleDateString()}`}
        {selectedItem.earlyDeliveryAt && selectedItem.lateDeliveryAt && ' - '}
        {selectedItem.lateDeliveryAt && new Date(selectedItem.lateDeliveryAt).toLocaleDateString()}
      </Typography>
    ) : check.earlyDeliveryAt || check.lateDeliveryAt ? (
      <Typography variant="caption">
        {t('delivery')}:{check.earlyDeliveryAt && ` ${new Date(check.earlyDeliveryAt).toLocaleDateString()}`}
        {check.earlyDeliveryAt && check.lateDeliveryAt && ' - '}
        {check.lateDeliveryAt && new Date(check.lateDeliveryAt).toLocaleDateString()}
      </Typography>
    ) : null;

  return (
    <ExtendedListItem
      onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
      secondaryAction={
        <>
          <Chip
            label={t(`riskLevel.${translationKey}`)}
            color={color}
            variant="outlined"
            size="small"
            icon={<Icon />}
          />
          <IconButton size="small" aria-label={isDetailsExpanded ? 'Collapse details' : 'Expand details'}>
            {isDetailsExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </>
      }
    >
      <ExtendedListItemText
        primary={title}
        secondary={
          <Stack component="span">
            {description}
            {deliveryInfo}
            {riskLevel === 'MEDIUM' && (
              <Typography variant="caption" color="error">
                {t('itemSelectionWarning')}
              </Typography>
            )}
          </Stack>
        }
      />
    </ExtendedListItem>
  );
};
